import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/lib/shadcn/ui/dialog";
import { Button } from "@/lib/shadcn/ui/button";
import { Input } from "@/lib/shadcn/ui/input";
import { Label } from "@/lib/shadcn/ui/label";
import Select, { MultiValue, SingleValue } from "react-select";
import { multiSelectDropdownStyle, singleSelectDropdownStyle } from "@/utils/graphs";
import { errorPopup } from "@/utils/alerts";
import { User } from "@/models/responses";
import { useAppData } from "@/hooks/useAppData";
import { SelectOption } from "@/lib/shadcn/components/searchable-select";

interface AddUserModalProps {
    open: boolean; // Changed from show
    onClose: () => void; // Changed from handleClose
    onSubmit: (firstName: string, lastName: string, email: string, isAdmin: boolean, visibleOwners: number[], existingUser?: User) => void; // Changed from handleSubmitted
    existingUser?: User;
}

const AddUserDialog: React.FC<AddUserModalProps> = ({ open, onClose, onSubmit, existingUser }) => {
    const { ownerOptions } = useAppData();
    const [firstName, setFirstName] = useState<string>(existingUser?.givenName || "");
    const [lastName, setLastName] = useState<string>(existingUser?.lastName || "");
    const [email, setEmail] = useState<string>(existingUser?.email || "");
    const [isEmailValid, setIsEmailValid] = useState(true);
    const [isAdmin, setIsAdmin] = useState<boolean>(existingUser?.isAdmin || false);
    const [visibleOwners, setVisibleOwners] = useState<number[]>(existingUser?.visibleOwners || []);

    useEffect(() => {
        setFirstName(existingUser?.givenName || "");
        setLastName(existingUser?.lastName || "");
        setEmail(existingUser?.email || "");
        setIsAdmin(existingUser?.isAdmin || false);
        setVisibleOwners(existingUser?.visibleOwners || []);
    }, [existingUser]);

    const validateAndSetEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const isValid = emailRegex.test(email);
        setIsEmailValid(isValid);
        setEmail(email);
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        try {
            onSubmit(firstName, lastName, email, isAdmin, visibleOwners, existingUser);
            handleClose();
        } catch (error) {
            errorPopup(`Failed to add user: ${error}`);
        }
    };

    const handleClose = () => {
        setFirstName("");
        setLastName("");
        setEmail("");
        setIsAdmin(false);
        setVisibleOwners([]);
        onClose();
    };

    const onVisibleOwnersChanged = (newValue: MultiValue<SelectOption>) => {
        setVisibleOwners(newValue.map((owner) => Number(owner.value)));
    };

    const onIsAdminChanged = (newValue: SingleValue<SelectOption>) => {
        setIsAdmin(newValue?.value === "true");
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle>{existingUser ? "Edit" : "Add"} User</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="firstName">First Name</Label>
                            <Input id="firstName" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="lastName">Last Name</Label>
                            <Input id="lastName" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
                        </div>
                    </div>

                    <div className="grid grid-cols-[1fr_auto] gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => validateAndSetEmail(e.target.value)}
                                className={email !== "" && !isEmailValid ? "border-red-500" : ""}
                                required
                            />
                        </div>

                        <div className="space-y-2 w-[120px]">
                            <Label>Admin</Label>
                            <Select
                                options={[
                                    { label: "Yes", value: "true" },
                                    { label: "No", value: "false" },
                                ]}
                                value={{
                                    label: isAdmin ? "Yes" : "No",
                                    value: isAdmin ? "true" : "false",
                                }}
                                onChange={onIsAdminChanged}
                                styles={singleSelectDropdownStyle}
                                isSearchable={false}
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label>Visible Owners</Label>
                        <Select
                            options={ownerOptions.sort((a, b) => a.label.localeCompare(b.label))}
                            isMulti
                            closeMenuOnSelect={false}
                            value={ownerOptions.filter((owner) => visibleOwners.includes(Number(owner.value)))}
                            onChange={onVisibleOwnersChanged}
                            styles={multiSelectDropdownStyle}
                        />
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button type="submit">{existingUser ? "Update" : "Add"}</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default AddUserDialog;
